import { useTranslation } from "react-i18next";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { formatInitials, formatName } from "@tellescope/utils";

interface CareTeamMemberSelectProps {
  careTeamUserIds: string[];
  value: string | undefined;
  onChange: (userId: string) => void;
  disabled?: boolean;
  id?: string;
}

export const CareTeamMemberSelect = ({ careTeamUserIds, value, onChange, disabled, id }: CareTeamMemberSelectProps) => {
  const { t } = useTranslation("messages");

  const usersQuery = useTellescopeQuery({
    model: "users",
    method: "getByIds",
    request: { ids: careTeamUserIds },
    enabled: careTeamUserIds.length > 0,
    select: (data) => [...(data?.matches ?? [])].sort((a, b) => formatName(a).localeCompare(formatName(b))),
  });

  const careTeamUsers = usersQuery.data ?? [];
  const isLoading = usersQuery.isLoading;

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled || isLoading}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={isLoading ? t("Loading care team...") : t("Select a care team member")} />
      </SelectTrigger>
      <SelectContent>
        {careTeamUsers.map((user) => {
          const displayName = formatName(user) || user.email || user.id;
          return (
            <SelectItem key={user.id} value={user.id}>
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback useStaticBackground id={user.id}>
                    {formatInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm truncate">{displayName}</span>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
